// src/components/CategoryGallery.js

import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "./Header";
import Breadcrumb from "../components/Breadcrumb";
import HeroScreen from "./HeroScreen";
import Footer from "./Footer";
import galleryData from "../data/galleryData.json";
import Zoom from "react-image-zooom";
import "./CategoryGallery.css";
import { getCloudinaryUrl } from "../utils/cloudinary";
import { useLanguage } from "../contexts/LanguageContext";
import Title from "../components/Title";
import ImageText from "../components/ImageText";
import { CLOUDINARY_IMAGES } from "../constants/images";

// ===================================================================
// LAZY LOADING IMAGE COMPONENT
// ===================================================================
// Optimized LazyImage component for CategoryGallery.js
// Replace the existing LazyImage component with this version

const LazyImage = ({ publicId, alt, className, onClick, width = 600, index = 0 }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!imgRef.current) return;

    // ⭐ OPTIMIZED: Larger rootMargin for smoother loading
    // 200px = ~half screen on desktop, 300px on mobile
    const rootMargin = window.innerWidth <= 768 ? '300px' : '200px';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: `${rootMargin} 0px ${rootMargin} 0px` } // Preload above AND below
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  // ⭐ NEW: Generate responsive sizes
  const thumbnailUrl = getCloudinaryUrl(publicId, { 
    width: 50, 
    quality: 30, 
    format: 'auto' 
  });

  const fullUrl = getCloudinaryUrl(publicId, { 
    width, 
    quality: 'auto', 
    format: 'auto' 
  });

  // Handle invalid URLs
  if (!thumbnailUrl || !fullUrl) return null;

  // ⭐ CRITICAL: First 12 images load eagerly for instant display
  const isAboveFold = index < 12;
  const loadingStrategy = isAboveFold ? 'eager' : 'lazy';
  const fetchPriority = index < 6 ? 'high' : 'auto';

  return (
    <div 
      ref={imgRef} 
      className={className}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {/* Blurred placeholder */}
      <img
        src={thumbnailUrl}
        alt={alt}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'blur(10px)',
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          transition: 'opacity 0.3s ease, transform 0.4s ease',
          opacity: isLoaded ? 0 : 1,
        }}
      />
      
      {/* Full quality image with native lazy loading */}
      {(isInView || isAboveFold) && (
        <img
          src={fullUrl}
          alt={alt}
          loading={loadingStrategy}
          decoding="async"
          fetchPriority={fetchPriority}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'opacity 0.3s ease, transform 0.4s ease',
            opacity: isLoaded ? 1 : 0,
          }}
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
};

// ===================================================================
// MAIN CATEGORY GALLERY COMPONENT
// ===================================================================
const CategoryGallery = () => {
  const { category } = useParams();
  const data = galleryData[category];

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [imageClasses, setImageClasses] = useState({});
  const [orderedImages, setOrderedImages] = useState([]);

  const { t } = useLanguage();

  const getCategoryTitle = () => {
    return t.categories?.[category] || data?.title || category;
  };

  const getCategoryTitleNoTones = () => {
    return t.categoriesNoTones?.[category] || data?.title || category;
  };

  useEffect(() => {
    if (!data || !data.images) return;

    const loadImages = async () => {
      const imagesInfo = [];

      await Promise.all(
        data.images.map((publicId) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              const aspect = img.width / img.height;
              let cls = "";
              if (aspect > 1.3) cls = "wide";
              else if (aspect < 0.8) cls = "tall";
              imagesInfo.push({ publicId, aspect, cls });
              resolve();
            };
            img.onerror = () => {
              console.warn(`Failed to detect proportions for: ${publicId}`);
              // Fallback: treat as normal size so it at least renders
              imagesInfo.push({ publicId, aspect: 1, cls: "" });
              resolve();
            };
            
            // Handle empty URLs safely before creating Image
            const src = getCloudinaryUrl(publicId, { width: 100, quality: 30 });
            if (src) {
                img.src = src;
            } else {
                resolve(); // Skip if URL generation failed
            }
          });
        })
      );

      const tall = imagesInfo.filter((i) => i.cls === "tall");
      const wide = imagesInfo.filter((i) => i.cls === "wide");
      const normal = imagesInfo.filter((i) => i.cls === "");

      const mixed = [];
      while (tall.length || wide.length || normal.length) {
        if (normal.length) mixed.push(normal.shift());
        if (wide.length) mixed.push(wide.shift());
        if (tall.length) mixed.push(tall.shift());
      }

      setImageClasses(
        mixed.reduce((acc, item) => {
          acc[item.publicId] = item.cls;
          return acc;
        }, {})
      );

      setOrderedImages(mixed.map((i) => i.publicId));
    };

    loadImages();
  }, [data]);

  // Preload logic
  useEffect(() => {
    if (selectedIndex === null || orderedImages.length === 0) return;
    
    const preload = (idx) => {
        const url = getCloudinaryUrl(orderedImages[idx], { 
            width: 2400, quality: 90, crop: 'limit' 
        });
        if (url) {
            const img = new Image();
            img.src = url;
        }
    };

    preload(selectedIndex);
    preload((selectedIndex + 1) % orderedImages.length);
    preload((selectedIndex - 1 + orderedImages.length) % orderedImages.length);

  }, [selectedIndex, orderedImages]);


  // Keyboard Navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (selectedIndex === null || !data) return;
      if (e.key === "Escape") setSelectedIndex(null);
      else if (e.key === "ArrowRight")
        setSelectedIndex((i) => (i + 1) % orderedImages.length);
      else if (e.key === "ArrowLeft")
        setSelectedIndex(
          (i) => (i - 1 + orderedImages.length) % orderedImages.length
        );
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedIndex, orderedImages, data]);

  if (!data) {
    return (
      <>
        <Header />
        <div className="category-gallery" style={{marginTop: "250px", fontFamily: "var(--font-main)", textAlign: "center"}}>
          <h1>{t.gallery.notAvailable}</h1>
          <Link to="/gallery" className="gallery-back">
            {t.gallery.return}
          </Link>
        </div>
        <ImageText 
          text={t.gallery.footerImageText} 
          image={CLOUDINARY_IMAGES.footer.gallery} 
          height="350px" 
          textSize="2rem"
        />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <HeroScreen 
        image={data.hero} 
        title={getCategoryTitleNoTones()} 
        height="70vh"
        title_size="5rem"
      />
      <Breadcrumb />

      <div className="category-gallery">
        <Title title={getCategoryTitle()} subtitle={t.gallery.category}/>

       <div className="category-grid">
          {orderedImages.map((publicId, i) => (
            <LazyImage
              key={i}
              publicId={publicId}
              alt={`${data.title} ${i + 1}`}
              className={`category-item ${imageClasses[publicId] || ""}`}
              onClick={() => setSelectedIndex(i)}
              width={600}
              index={i} // ⭐ NEW: Pass index for smart loading
            />
          ))}
        </div>

        <div>
          <Link to="/gallery" className="gallery-back">
            {t.gallery.return}
          </Link>
        </div>
      </div>

      {/* --- LIGHTBOX --- */}
      {selectedIndex !== null && (
        <div className="lightbox" onClick={() => setSelectedIndex(null)}>
          <span
            className="nav-arrow left"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(
                (selectedIndex - 1 + orderedImages.length) %
                  orderedImages.length
              );
            }}
          >
            ‹
          </span>

          <div
            className="zoom-wrapper"
            onClick={(e) => e.stopPropagation()}
          >
             {/* Only render Zoom if we have a valid URL */}
            {getCloudinaryUrl(orderedImages[selectedIndex]) && (
                <Zoom
                src={getCloudinaryUrl(orderedImages[selectedIndex], { 
                    width: 2400,      
                    quality: 90,      
                    crop: 'limit'     
                })}
                alt={`Full view ${selectedIndex + 1}`}
                zoom="200"
                transitionTime={0.4}
                className="lightbox-image"
                />
            )}
          </div>

          <span
            className="nav-arrow right"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex((selectedIndex + 1) % orderedImages.length);
            }}
          >
            ›
          </span>

          <span
            className="close"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(null);
            }}
          >
            ×
          </span>
        </div>
      )}
      <ImageText 
        text={t.gallery.footerImageText} 
        image={CLOUDINARY_IMAGES.footer.gallery} 
        height="350px" 
        textSize="2rem"
      />
      <Footer />
    </>
  );
};

export default CategoryGallery;